import {
  Children,
  createContext,
  use,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from "react"

import { twMerge } from "tailwind-merge"

import { Bee } from "~/src/components/BeeKit"

type ContextType = {
  setProvidedImages?: (children: ReactNode) => void
  setProvidedThumbnails?: (children: ReactNode) => void
}
const ImageCarouselContext = createContext<ContextType>({})

export function useImageCarouselContext() {
  const context = use(ImageCarouselContext)

  if (context == null) {
    throw new Error("Image Carousel components must be wrapped in <ImageCarousel />")
  }

  return context
}

export type ImageCarouselOptions = {
  selectedIndex?: number
  defaultSelectedIndex?: number
  onSelectIndex?: (index: number) => void
  className?: string
  imageContainerClassName?: string
  thumbnailContainerClassName?: string
  thumbnailClassName?: string
  selectedThumbnailClassName?: string
}
export type ImageCarouselProps = { children?: ReactNode } & ImageCarouselOptions

export function ImageCarousel(props: ImageCarouselProps) {
  const {
    children,
    selectedIndex,
    defaultSelectedIndex,
    onSelectIndex,
    className,
    imageContainerClassName,
    thumbnailContainerClassName,
    thumbnailClassName,
    selectedThumbnailClassName,
  } = props

  const [providedImages, setProvidedImages] = useState<ReactNode>(null)
  const [providedThumbnails, setProvidedThumbnails] = useState<ReactNode>(null)
  const [currentlySelectedIndex, innerSetCurrentlySelectedIndex] = useState<number>(defaultSelectedIndex || 0)

  const setCurrentlySelectedIndex = (index: number) => {
    innerSetCurrentlySelectedIndex(index)
    onSelectIndex?.(index)
  }

  useEffect(() => {
    if (selectedIndex != null && selectedIndex != currentlySelectedIndex) {
      innerSetCurrentlySelectedIndex(selectedIndex)
    }
  }, [selectedIndex])

  const providerValue = useMemo(
    () => ({
      setProvidedImages,
      setProvidedThumbnails,
    }),
    [setProvidedImages, setProvidedThumbnails]
  )

  return (
    <>
      <ImageCarouselContext value={providerValue}>{children}</ImageCarouselContext>
      <div className={twMerge("flex flex-col items-center", className)}>
        <Bee.Carousel
          className={imageContainerClassName || ""}
          value={currentlySelectedIndex}
          onValueChange={setCurrentlySelectedIndex}
          showDots={false}
          showArrows={true}
        >
          {providedImages}
        </Bee.Carousel>
        <div className={twMerge("flex justify-center gap-4", thumbnailContainerClassName)}>
          {Children.map(providedThumbnails, (child, index) => {
            return (
              <div
                tabIndex={0}
                className={
                  index == currentlySelectedIndex
                    ? twMerge(
                        "h-14 w-14 cursor-pointer",
                        thumbnailClassName,
                        "border-navy-800 rounded-lg border",
                        selectedThumbnailClassName
                      )
                    : twMerge("h-14 w-14 cursor-pointer", thumbnailClassName)
                }
                onClick={() => setCurrentlySelectedIndex(index)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    return setCurrentlySelectedIndex(index)
                  }
                }}
              >
                {child}
              </div>
            )
          })}
        </div>
      </div>
    </>
  )
}

ImageCarousel.Images = function Images(props: PropsWithChildren<object>) {
  const { children } = props
  const { setProvidedImages } = useImageCarouselContext()

  useEffect(() => {
    if (setProvidedImages) {
      setProvidedImages(children)
    }
  }, [children, setProvidedImages])

  return null
}

ImageCarousel.Thumbnails = function Thumbnails(props: PropsWithChildren<object>) {
  const { children } = props
  const { setProvidedThumbnails } = useImageCarouselContext()

  useEffect(() => {
    if (setProvidedThumbnails) {
      setProvidedThumbnails(children)
    }
  }, [children, setProvidedThumbnails])

  return null
}
